// TODO: rework Button component with new TS component from /atoms/Button
import React from 'react';
import { useUIDSeed } from 'react-uid';

import subYears from 'date-fns/sub_years';

import { useFormik } from 'formik';
import Postcode from 'postcode';
import * as Yup from 'yup';

import { FormContext } from '@zego/forms/Form';
import FormActions from '@zego/forms/FormActions';
import FormField from '@zego/forms/FormField';
import FormFieldGroup from '@zego/forms/FormFieldGroup';
import Input from '@zego/forms/Input';
import LicencePlateInput from '@zego/forms/LicencePlateInput';
import SplitDateInput from '@zego/forms/SplitDateInput';
import getStorage from '@zego/getStorage';
import { BREAKPOINTS, unit } from '@zego/theme';
import { isValidUKRegistrationNumber } from '@zego/utils';

import H3 from '@zego/components/atoms/H3';
import RegularText from '@zego/components/atoms/RegularText';
import Spacer from '@zego/components/atoms/Spacer';
import Button from '@zego/components/button';
import Link from '@zego/components/Link';
import ScopedNotification, {
  UiStatus,
} from '@zego/components/ScopedNotification';

import LicencePlate from '@zego/static/svgs/LicencePlate.svg';

const INITIAL_VALUES_KEY = 'carQuickEstimatesInitialValues';

function Form(props) {
  const seed = useUIDSeed();

  return (
    <FormContext.Provider value={seed}>
      <form {...props} />
    </FormContext.Provider>
  );
}

function getDefaultInitialValues() {
  const storage = getStorage('local');
  const defaultInitialValues = storage && storage.getItem(INITIAL_VALUES_KEY);

  if (defaultInitialValues) {
    return defaultInitialValues;
  }

  return {
    registration: '',
    bday: '',
    postcode: '',
  };
}

const validationSchema = Yup.object().shape({
  registration: Yup.string()
    .required('Required')
    .test(
      'is-valid-registration',
      'Must be a valid UK vehicle registration',
      value => {
        if (!value) {
          return false;
        }
        return isValidUKRegistrationNumber(value);
      },
    ),
  bday: Yup.date()
    .min(subYears(new Date(), 90), 'Too long ago')
    .max(new Date(), 'Cannot be in the future')
    .required('Required'),
  postcode: Yup.string()
    .required('Required')
    .test('is-valid-postcode', 'Must be a valid UK postcode', value => {
      if (!value || typeof value !== 'string') {
        return false;
      }
      return Postcode.isValid(value.trim());
    }),
});

const BdayAndPostcodeFields = ({ formik }) => (
  <div
    css={{
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      [BREAKPOINTS.large]: { flexDirection: 'row' },
    }}
  >
    <FormField
      name="bday"
      label="Date of birth"
      value={formik.values.bday}
      error={formik.errors.bday}
      widget={SplitDateInput}
      widgetProps={{
        showDay: true,
      }}
      onChange={value => formik.setFieldValue('bday', value, false)}
      css={{
        [BREAKPOINTS.large]: {
          width: 'calc(60% - 16px)',
          marginRight: unit(2),
        },
      }}
    />
    <FormField
      name="postcode"
      placeholder="WR2 D2E"
      label="Postcode"
      textTransform="uppercase"
      value={formik.values.postcode}
      error={formik.errors.postcode}
      widget={Input}
      onChange={event =>
        formik.setFieldValue('postcode', event.target.value, false)
      }
      css={{
        marginTop: unit(3),
        [BREAKPOINTS.large]: { marginTop: 0, width: '40%' },
      }}
    />
  </div>
);

const RegistrationNumberField = ({ formik, registrationNumberLabel }) => (
  <FormField
    label={registrationNumberLabel}
    icon={LicencePlate}
    name="registration"
    placeholder="NK55 NR1"
    textTransform="uppercase"
    value={formik.values.registration}
    error={formik.errors.registration}
    widget={LicencePlateInput}
    size="large"
    onChange={event =>
      formik.setFieldValue('registration', event.target.value, false)
    }
    css={{
      marginBottom: unit(1),
    }}
  />
);

export default function CarQuickEstimatesFormCore({
  submitting,
  onSubmit,
  className,
  initialValues = getDefaultInitialValues(),
  error,
  id,
  switchFieldsOrder,
  hideTitle,
  registrationNumberLabel = 'Enter your car’s registration number',
}) {
  const handleSubmit = (values, actions) => {
    const storage = getStorage('local');
    storage && storage.setItem(INITIAL_VALUES_KEY, values);
    onSubmit(values, () => actions.setSubmitting(false));
  };

  const formik = useFormik({
    initialValues,
    validationSchema,
    onSubmit: handleSubmit,
  });

  return (
    <Form id={id} onSubmit={formik.handleSubmit} className={className}>
      {error && (
        <ScopedNotification
          status={UiStatus.Error}
          message="Something went wrong."
        />
      )}
      {hideTitle ? null : (
        <>
          <H3
            css={{
              fontSize: 22,
              lineHeight: '28px',
              [BREAKPOINTS.small]: { fontSize: 22, lineHeight: '28px' },
            }}
          >
            Get an estimate in seconds
          </H3>
          <Spacer height={unit(2)} />
        </>
      )}
      <FormFieldGroup spacing={2}>
        {switchFieldsOrder ? (
          <>
            <RegistrationNumberField
              formik={formik}
              registrationNumberLabel={registrationNumberLabel}
            />
            <Spacer height={unit(2)} />
            <BdayAndPostcodeFields formik={formik} />
          </>
        ) : (
          <>
            <BdayAndPostcodeFields formik={formik} />
            <Spacer height={unit(2)} />
            <RegistrationNumberField
              formik={formik}
              registrationNumberLabel={registrationNumberLabel}
            />
          </>
        )}
      </FormFieldGroup>
      <FormActions>
        <Button type="submit" stretch={true} submitting={submitting}>
          {error ? 'Try again' : 'Get my estimate'}
        </Button>
      </FormActions>
      <RegularText
        css={{
          marginTop: unit(1),
          fontSize: 12,
          fontWeight: 'normal',
          textAlign: 'center',
        }}
      >
        By continuing you are agreeing to our{' '}
        <Link href="/privacy">Privacy Policy.</Link>
      </RegularText>
    </Form>
  );
}
